import { Link } from "react-router-dom";
import { MessageCircle, MapPin, Ruler, Boxes, ClipboardList, Wrench, Truck, Cog, TestTube2, GraduationCap, Headphones, PenTool, ArrowRight, ArrowLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { WHATSAPP_URL } from "../lib/i18n";
import { useSiteImages } from "../hooks/use-site-images";

const STEP_ICONS = [MessageCircle, MapPin, PenTool, Boxes, ClipboardList, Wrench, Truck, Cog, TestTube2, GraduationCap, Headphones];

// Small illustrative photos for each step
const STEP_IMAGES = [
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=500&q=75", // Consultation - people meeting
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=500&q=75", // Site visit
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=500&q=75", // Planning - blueprint
  "https://images.unsplash.com/photo-1556910096-6f5e72db6803?auto=format&fit=crop&w=500&q=75", // Equipment selection
  "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=500&q=75", // BOQ document
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=500&q=75", // Fabrication
  "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=500&q=75", // Supply / delivery truck
  "https://images.unsplash.com/photo-1581093458791-9d15482442f6?auto=format&fit=crop&w=500&q=75", // Installation
  "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=500&q=75", // Testing
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=500&q=75", // Training
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=500&q=75", // Support
];

const STEPS_EN = [
  { title: "Consultation", text: "Discuss the project idea, menu, capacity, budget, and timeline." },
  { title: "Site Visit", text: "Inspect the site — dimensions, utilities, ventilation, delivery access, installation conditions." },
  { title: "Kitchen Planning", text: "Workflow-based planning focused on speed, hygiene, safety and staff movement." },
  { title: "Equipment Selection", text: "Choose the right equipment based on menu, production capacity, project type and budget." },
  { title: "BOQ Preparation", text: "Clear bill of quantities with specifications, sizes and scope." },
  { title: "Stainless Steel Fabrication", text: "In-house manufacturing of custom tables, sinks, counters, shelves, hoods and worktops." },
  { title: "Supply & Delivery", text: "Logistics coordinated to your project timeline." },
  { title: "Installation", text: "On-site installation by experienced technicians." },
  { title: "Testing & Commissioning", text: "Full testing and safe hand-over ready for operation." },
  { title: "Training", text: "Guide your team on operation, cleaning and daily care." },
  { title: "Maintenance & Support", text: "After-sales support, maintenance contracts and spare parts." },
];

const STEPS_AR = [
  { title: "الاستشارة", text: "نناقش فكرة المشروع والمنيو والطاقة الاستيعابية والميزانية والجدول الزمني." },
  { title: "زيارة الموقع", text: "معاينة الموقع — الأبعاد والمرافق والتهوية ومنافذ التوريد وظروف التركيب." },
  { title: "تخطيط المطبخ", text: "تخطيط قائم على انسيابية العمل يركز على السرعة والنظافة والسلامة وحركة الطاقم." },
  { title: "اختيار المعدات", text: "اختيار المعدات المناسبة بناءً على المنيو والإنتاجية ونوع المشروع والميزانية." },
  { title: "إعداد قائمة الكميات (BOQ)", text: "قائمة كميات واضحة بالمواصفات والمقاسات والنطاق." },
  { title: "تصنيع الستانلس ستيل", text: "تصنيع داخلي مخصص للطاولات والأحواض والأسطح والأرفف والشفاطات." },
  { title: "التوريد والتسليم", text: "لوجستيات وتنسيق التوريد وفق الجدول الزمني للمشروع." },
  { title: "التركيب", text: "تركيب في الموقع بواسطة فنيين ذوي خبرة." },
  { title: "الاختبار والتشغيل", text: "اختبار كامل وتسليم آمن جاهز للتشغيل." },
  { title: "التدريب", text: "توجيه الطاقم على التشغيل والتنظيف والعناية اليومية." },
  { title: "الصيانة والدعم", text: "دعم ما بعد البيع وعقود الصيانة وقطع الغيار." },
];

export default function HowWeWork() {
  const { lang, isRTL } = useLang();
  const siteImage = useSiteImages();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const steps = (lang === "ar" ? STEPS_AR : STEPS_EN);

  return (
    <div data-testid="how-we-work-page">
      {/* Hero (lighter) */}
      <section className="relative py-20 bg-gradient-to-br from-cyan-50 via-white to-red-50/30 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-4 font-semibold uppercase">
            {lang === "ar" ? "منهجيتنا" : "OUR METHODOLOGY"}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl text-[#0891b2]" data-testid="hww-title">
            {lang === "ar" ? "كيف نعمل — من الفكرة إلى التسليم" : "How We Work — from concept to hand-over"}
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl">
            {lang === "ar"
              ? "منهجية 11 خطوة موثوقة لبناء مطابخ تعمل بكفاءة."
              : "An 11-step proven methodology to deliver kitchens that perform."}
          </p>
        </div>
      </section>

      {/* Steps with images */}
      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-5">
            {steps.map((s, i) => {
              const Icon = STEP_ICONS[i] || MessageCircle;
              return (
                <div
                  key={i}
                  data-testid={`hww-step-${i}`}
                  className="group flex bg-white border border-slate-200 hover:border-[#0891b2] hover:shadow-xl rounded-2xl overflow-hidden transition-all"
                >
                  {/* Small image */}
                  <div className="relative w-32 sm:w-40 shrink-0 bg-slate-100 overflow-hidden">
                    <img
                      src={siteImage(`how_we_work_step_${i + 1}`, STEP_IMAGES[i])}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0891b2]/60 via-transparent to-transparent" />
                    <div className="absolute top-3 start-3 font-display text-3xl text-white/90 leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="absolute bottom-3 start-3 w-9 h-9 rounded-lg bg-white/95 flex items-center justify-center shadow-md">
                      <Icon className="w-4 h-4 text-[#ef4444]" />
                    </div>
                  </div>
                  <div className="p-5 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">
                      {lang === "ar" ? `الخطوة ${i + 1}` : `Step ${i + 1}`}
                    </div>
                    <h3 className="font-display text-lg text-[#0891b2] mb-2 leading-snug">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA (lighter) */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 via-white to-red-50/30">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] mb-4">
            {lang === "ar" ? "جاهز نبدأ مشروعك؟" : "Ready to start your project?"}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/quote"><Button data-testid="hww-cta-quote" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-8 font-semibold">
              {lang === "ar" ? "اطلب عرض سعر مجاني" : "Request Free Quotation"} <Arrow className="w-4 h-4 ms-2" />
            </Button></Link>
            <Link to="/consultation"><Button variant="outline" data-testid="hww-cta-visit" className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white h-12 px-6 font-semibold">
              {lang === "ar" ? "احجز استشارة" : "Book Consultation"}
            </Button></Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer"><Button className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 font-semibold" data-testid="hww-cta-wa">
              <MessageCircle className="w-4 h-4 me-2" />{lang === "ar" ? "واتساب" : "WhatsApp"}
            </Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
